// Import package components
import {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import UIkit from "uikit";
import { AppContexts } from "../../providers";
import ReservationsService from "../../services/reservations.service";
import Card from "./card";
import AddTextLine from "../common/AddTextLine";

const CANDIDATES_PER_PAGE = 50;

const ExtendGuestStay = () => {
  const settingsCtx = useContext(AppContexts.SettingsContext);
  const requestIdRef = useRef(0);

  const [filters, setFilters] = useState({
    start_date: moment().format("YYYY-MM-DD"),
    min_gap: 0,
    max_gap: 99999,
    extend_stay: "check_out",
    date_range: 7,
    property_id: "",
    tag: "",
  });

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // To leave the legacy variables in place
  let {
    start_date: startDate,
    min_gap: minGap,
    max_gap: maxGap,
    extend_stay: extendStay,
    date_range: dateRange,
    property_id: propertyId,
    tag: tag,
  } = filters;

  useEffect(() => {
    settingsCtx.get(["tags"]);
  }, []);

  const loadCandidates = useCallback(
    async (nextPage = 0, append = false) => {
      if (!startDate) return;

      const requestId = ++requestIdRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoaded(false);
        setLoadError(false);
      }

      try {
        const response = await ReservationsService.getExtendStayCandidates({
          start_date: startDate,
          min_gap: minGap,
          max_gap: maxGap,
          extend_stay: extendStay,
          date_range: dateRange,
          property_id: propertyId,
          tag,
          page: nextPage,
          per_page: CANDIDATES_PER_PAGE,
        });

        if (requestId !== requestIdRef.current) return;

        const rows = response.data?.paginated || [];
        setCandidates((prev) => (append ? [...prev, ...rows] : rows));
        setTotalCandidates(Number(response.data?.total || 0));
        setPage(nextPage);
        setLoaded(true);
      } catch (err) {
        console.log("Error loading extend guest stay bookings", err);
        if (requestId === requestIdRef.current) {
          setLoadError(true);
          setLoaded(true);
          UIkit.notification(
            "Bookings could not be loaded. Please try again.",
            "danger",
          );
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingMore(false);
        }
      }
    },
    [startDate, minGap, maxGap, extendStay, dateRange, propertyId, tag],
  );

  useEffect(() => {
    setSelected([]);
    loadCandidates(0, false);
  }, [loadCandidates]);

  const getClassName = (reservation) => {
    const classes = {
      OWN: "owner",
      BLK: "block",
      STA: "guest",
    };

    let classList = [];
    if (selected.includes(reservation.guest_reservation_id)) {
      classList.push("selected");
    }

    classList.push(classes[reservation.category] || "guest");

    return classList.join(" ");
  };

  const hasFlag = (reservation, flag) => {
    return (reservation.flags || []).find((f) => f.name === flag);
  };

  const hasMoreCandidates = candidates.length < totalCandidates;

  const loadMoreCandidates = useCallback(() => {
    if (!loadingMore && hasMoreCandidates) {
      loadCandidates(page + 1, true);
    }
  }, [hasMoreCandidates, loadCandidates, loadingMore, page]);

  const targetIndex = useMemo(() => {
    return dateRange === "" ? null : Number(dateRange) + 1;
  }, [dateRange]);

  const tags = useMemo(
    () => settingsCtx.settings.tags || [],
    [settingsCtx.settings],
  );

  const handleFilter = (key, e) => {
    let { max_gap, min_gap } = filters;
    let { value } = e.target;

    if (key === "min_gap" && value > max_gap) {
      return;
    }

    if (key === "max_gap" && value < min_gap) {
      return;
    }

    setFilters({ ...filters, [key]: e.target.value });
  };

  const handleTarget = (id) => {
    setSelected((prev) => {
      let temp = prev.findIndex((item) => item == id);
      if (temp >= 0) {
        return [...prev.slice(0, temp), ...prev.slice(temp + 1)];
      } else {
        return [...prev, id];
      }
    });
  };

  const handleExtensionsSent = useCallback((formData) => {
    const flagName = formData.orientation + "_extension_sent";
    setCandidates((prev) =>
      prev.map((candidate) => ({
        ...candidate,
        reservations: (candidate.reservations || []).map((reservation) => {
          if (
            !formData.reservation_ids.includes(
              reservation.guest_reservation_id,
            )
          ) {
            return reservation;
          }

          return {
            ...reservation,
            flags: [
              ...(reservation.flags || []),
              {
                item_id: reservation.guest_reservation_id,
                name: flagName,
              },
            ],
          };
        }),
      })),
    );
  }, []);

  const renderer = (rental) => {
    let reservations = rental.reservations || [];

    return (
      <ul
        key={`extend-stay-list-${rental.rental_id || rental.pms_id}`}
        className="extend-stays-outer-grid uk-width-1-1"
      >
        <li className="extend-stays-property-id">{rental.pms_id}</li>
        <li>
          <ul className="extend-stays-inner-grid">
            {[...new Array(15)].map((_, index) => (
              <li
                key={`${rental.rental_id}-${index}`}
                className={index === targetIndex ? "extend-stay-target" : ""}
              ></li>
            ))}

            {reservations.map((reservation) => {
              let width = (1 / 15) * 100;
              let left = moment
                .utc(reservation.check_in)
                .startOf("day")
                .diff(
                  moment.utc(startDate).add(-1, "days").startOf("day"),
                  "days",
                );
              let right = moment
                .utc(reservation.check_out)
                .startOf("day")
                .diff(
                  moment.utc(startDate).add(-1, "days").startOf("day"),
                  "days",
                );

              return (
                <li
                  key={`${rental.rental_id}-${reservation.guest_reservation_id}`}
                  className={getClassName(reservation)}
                  style={{
                    left: `${(left + 0.6) * width}%`,
                    right: `${100 - (right + 0.4) * width}%`,
                  }}
                  onClick={() => handleTarget(reservation.guest_reservation_id)}
                  uk-tooltip={`
                      title: <div class='hms-extend-card'><div class='uk-grid uk-padding-small'><div class='uk-width-1-1@s uk-margin-tiny-bottom'><label class='uk-form-label'>NAME</span><div>${
                        reservation.name
                      }</div></div><div class='uk-width-1-1@s uk-margin-top uk-margin-tiny-bottom'><label class='uk-form-label'>CHECK-IN/CHECK-OUT</label><div>${moment
                        .utc(reservation.check_in)
                        .format("MM/DD/YYYY")} - ${moment
                        .utc(reservation.check_out)
                        .format(
                          "MM/DD/YYYY",
                        )}</div></div><div class='uk-width-1-1@s uk-margin-top'><label class='uk-form-label'>PHONE</label><div>${
                        reservation.phone
                      } </div></div><div class='uk-width-1-1@s uk-margin-top'><label class='uk-form-label'>EMAIL</label><div> ${
                        reservation.email
                      }</div></div></div></div>;
                      container: #hms-extendStays;
                      delay: 300;`}
                >
                  {hasFlag(reservation, "check_in_extension_sent") && (
                    <div className="check_in_extension_sent"></div>
                  )}
                  {hasFlag(reservation, "check_out_extension_sent") && (
                    <div className="check_out_extension_sent"></div>
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      </ul>
    );
  };

  return (
    <>
      <section id="hms-extendStays">
        <AddTextLine feature="Extend Guest Stay" />
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Extend Guest Stay</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex">
            <div className="uk-width-2-3">
              <div className="uk-card uk-card-small uk-card-default hms-form-card">
                <div className="uk-overflow-auto">
                  <form className="uk-form uk-flex uk-flex-middle extend-stay-filters hms-form">
                    <div className="uk-inline">
                      <label className="uk-form-label" htmlFor="stayStartDate">
                        Start Date
                      </label>
                      <div className="uk-form-controls hms-date-picker">
                        <input
                          type="date"
                          id="stayStartDate"
                          value={filters.start_date}
                          onInput={(e) => handleFilter("start_date", e)}
                          min={moment().format("YYYY-MM-DD")}
                        />
                      </div>
                    </div>
                    <div className="uk-inline rbr-min-gap">
                      <label className="uk-form-label">Min Gap</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            value={filters.min_gap}
                            onInput={(e) => handleFilter("min_gap", e)}
                          >
                            <option value={0}>Show All</option>
                            {[...new Array(6)].map((_, index) => (
                              <option
                                value={`${index + 1}`}
                                key={`gap_${index + 1}`}
                              >
                                {index + 1}
                              </option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-inline rbr-max-gap">
                      <label className="uk-form-label">Max Gap</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            value={filters.max_gap}
                            onInput={(e) => handleFilter("max_gap", e)}
                          >
                            <option value={99999}>Show All</option>
                            {[...new Array(6)].map((_, index) => (
                              <option
                                value={`${index + 1}`}
                                key={`gap_${index + 1}`}
                              >
                                {index + 1}
                              </option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-inline extend-stay-dropdown">
                      <label className="uk-form-label">Extend Stay</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            value={filters.extend_stay}
                            onInput={(e) => handleFilter("extend_stay", e)}
                          >
                            <option value="check_in">Early Check-In</option>
                            <option value="check_out">Late Check-Out</option>
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span>
                              {extendStay === "check_in"
                                ? "Early Check-In"
                                : extendStay === "check_out"
                                  ? "Late Check-Out"
                                  : ""}
                            </span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-inline">
                      <label className="uk-form-label">
                        {extendStay == "check_in" ? "Arriving" : "Departing"} In
                      </label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            value={filters.date_range}
                            onInput={(e) => handleFilter("date_range", e)}
                          >
                            <option value="">Any Day</option>
                            {[...new Array(8)].map((_, index) => (
                              <option value={index} key={`date_${index}`}>
                                {`${index} Days - ${moment(startDate)
                                  .add(index, "days")
                                  .format("ddd")}, ${moment(startDate)
                                  .add(index, "days")
                                  .format("MM/DD")}`}
                              </option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span>
                              {dateRange == ""
                                ? "Any Day"
                                : `${dateRange} Days - ${moment(startDate)
                                    .add(dateRange, "days")
                                    .format("ddd")}, ${moment(startDate)
                                    .add(dateRange, "days")
                                    .format("MM/DD")}`}
                            </span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-inline">
                      <label className="uk-form-label">Tag</label>
                      <div className="uk-form-controls">
                        <div uk-form-custom="target: > * > span:first-child">
                          <select
                            value={filters.tag}
                            onInput={(e) => handleFilter("tag", e)}
                          >
                            <option value="">All Tags</option>
                            {tags.map((t, idx) => (
                              <option value={t.id} key={`tag_${idx}`}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span>{tag == "" ? "All Tags" : tag}</span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="uk-inline">
                      <label className="uk-form-label">Property ID</label>
                      <div className="uk-form-controls">
                        <div className="uk-search uk-search-default uk-form-controls">
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          ></span>
                          <input
                            className="uk-search-input"
                            type="search"
                            value={filters.property_id}
                            onInput={(e) => handleFilter("property_id", e)}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                  <div>Select guests by clicking on the blue lines.</div>
                  <div className="extend-stays-grid">
                    <ul className="extend-stays-outer-grid extend-stays-grid-header uk-width-1-1">
                      <li className="extend-stays-property-id">Property ID</li>
                      <li>
                        <ul className="extend-stays-inner-grid">
                          {[...new Array(15)].map((_, index) => (
                            <li
                              key={`header-${index}`}
                              className={
                                index === dateRange + 1
                                  ? "extend-stay-target"
                                  : ""
                              }
                            >
                              <div>
                                {moment(startDate)
                                  .add(index - 1, "days")
                                  .format("MM/DD")}
                              </div>
                              <div>
                                {moment(startDate)
                                  .add(index - 1, "days")
                                  .format("ddd")}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>

                    {!loaded ? (
                      <div className="extend-stays-grid-empty uk-padding uk-width-1-1 uk-text-center">
                        <div data-uk-spinner="ratio: 2;"></div>
                      </div>
                    ) : loadError ? (
                      <div className="extend-stays-grid-empty uk-padding uk-width-1-1 uk-text-left">
                        <div>
                          Bookings could not be loaded. Please change the start
                          date or try refreshing the page.
                        </div>
                      </div>
                    ) : candidates.length === 0 ? (
                      <div className="extend-stays-grid-empty uk-padding uk-width-1-1 uk-text-left">
                        <div>
                          No bookings found. Please change the filters and try
                          again.
                        </div>
                      </div>
                    ) : (
                      <div
                        id="hms-extend-stays-scrollable"
                        className="extend-stays-grid-body"
                      >
                        <InfiniteScroll
                          dataLength={candidates.length}
                          next={loadMoreCandidates}
                          hasMore={hasMoreCandidates}
                          loader={(
                            <div className="extend-stays-grid-loading uk-padding-small uk-text-center">
                              <span data-uk-spinner="ratio: 1;"></span>
                            </div>
                          )}
                          scrollableTarget="hms-extend-stays-scrollable"
                        >
                          {candidates.map(renderer)}
                        </InfiniteScroll>
                        {hasMoreCandidates && (
                          <div className="extend-stays-grid-count uk-text-center">
                            Showing {candidates.length} of {totalCandidates}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="uk-margin-top uk-flex uk-flex-middle hms-extendStay-user-colors">
                <div>Guest</div>
                <div>Owner</div>
                <div>Block</div>
              </div>
              <div className="uk-width-1-1 uk-margin-top footnote">
                Please cross-check the accuracy of owner reservations and
                maintenance blocks on the calendar above in your rental
                software.
              </div>
            </div>

            <div className="uk-width-1-3">
              <Card
                selectedIds={selected}
                setSelected={setSelected}
                onExtensionsSent={handleExtensionsSent}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ExtendGuestStay;
